// src/lib/onboarding/profileGateResumeProgress.js

const STORAGE_KEY =
  "gwin_profile_gate_resume_v2";

const LEGACY_STORAGE_KEY =
  "gwin_profile_gate_resume_step";

// =========================================================
// CURRENT PROFILE GATE STEPS
// =========================================================
//
// Current onboarding:
//
// welcome
// → get-started
// → focus
// → business-basics
// → financial-setup
// → summary
// → learning-plan
// → orientation
// → onboarding-complete
// → consent-notices
//
// =========================================================

export const PROFILE_GATE_V2_STEPS = [
  "welcome",
  "get-started",
  "focus",
  "business-basics",
  "financial-setup",
  "summary",
  "learning-plan",
  "orientation",
  "onboarding-complete",
  "consent-notices",
];

// =========================================================
// LEGACY ROUTES
// =========================================================

export const LEGACY_PROFILE_GATE_STEPS = [
  "full-name",
  "phone",
  "personal-congrats",

  "app-reason",

  "business-profile",
  "business-size",
  "financial-challenge",
  "journey-stage",

  "business-name",
  "legal-nature",
  "registration-number",
  "industry",
  "revenue",
  "business-age",
  "address",

  "business-congrats",

  "business-basics-complete",
  "business-stage",
  "business-stage-complete",

  "financial-setup-complete",

  "priorities",
  "priorities-complete",

  "confidence",
  "confidence-complete",

  "review",

  "gift",

  "intro-step",
  "intro-video",

  "consent",
  "legal-consent",
  "consent-notice",
  "consent-notices-step",
];

export const PROFILE_GATE_STEPS =
  Array.from(
    new Set([
      ...PROFILE_GATE_V2_STEPS,
      ...LEGACY_PROFILE_GATE_STEPS,
    ])
  );

// =========================================================
// LEGACY → CURRENT ROUTE MAP
// =========================================================

const STEP_ALIASES = {
  // Old personal setup
  "full-name":
    "get-started",

  phone:
    "get-started",

  "personal-congrats":
    "get-started",

  // Old focus/reason screens
  "app-reason":
    "get-started",

  priorities:
    "focus",

  "priorities-complete":
    "business-basics",

  "financial-challenge":
    "focus",

  // Old business screens
  "business-profile":
    "business-basics",

  "business-name":
    "business-basics",

  "legal-nature":
    "business-basics",

  "registration-number":
    "business-basics",

  industry:
    "business-basics",

  address:
    "business-basics",

  "business-size":
    "business-basics",

  "business-age":
    "business-basics",

  "business-stage":
    "business-basics",

  "business-basics-complete":
    "financial-setup",

  "business-stage-complete":
    "financial-setup",

  // Old finance screens
  revenue:
    "financial-setup",

  "financial-setup-complete":
    "summary",

  "business-congrats":
    "summary",

  // Old summary screens
  confidence:
    "summary",

  "confidence-complete":
    "summary",

  review:
    "summary",

    // Old notification step has been removed.
  notifications: "consent-notices",

  // Old final screen
  gift:
    "onboarding-complete",

  "intro-step":
    "onboarding-complete",

  "intro-video":
    "onboarding-complete",

  // Consent aliases
  consent:
    "consent-notices",

  "legal-consent":
    "consent-notices",

  "consent-notice":
    "consent-notices",

  "consent-notices-step":
    "consent-notices",
};

// =========================================================
// GENERAL HELPERS
// =========================================================

function cleanProfileGateValue(
  value
) {
  return String(
    value || ""
  )
    .trim()
    .replace(
      /^https?:\/\/[^/]+/i,
      ""
    )
    .split("?")[0]
    .split("#")[0]
    .replace(
      /^\/+|\/+$/g,
      ""
    );
}

function hasText(
  value
) {
  return (
    String(
      value || ""
    ).trim().length >
    0
  );
}

function hasExactly(
  value,
  count
) {
  return (
    Array.isArray(
      value
    ) &&
    value.length ===
      count
  );
}

// =========================================================
// GENERIC NORMALISATION
// =========================================================

export function normalizeProfileGateStep(
  stepOrPath
) {
  const cleanValue =
    cleanProfileGateValue(
      stepOrPath
    );

  const possibleStep =
    cleanValue.startsWith(
      "profile-gate/"
    )
      ? cleanValue
          .replace(
            /^profile-gate\/?/,
            ""
          )
          .split("/")[0]
      : cleanValue
          .split("/")[0];

  const normalizedStep =
    STEP_ALIASES[
      possibleStep
    ] ||
    possibleStep;

  if (
    PROFILE_GATE_V2_STEPS.includes(
      normalizedStep
    )
  ) {
    return normalizedStep;
  }

  return "welcome";
}

export function profileGatePathForStep(
  stepOrPath
) {
  const step =
    normalizeProfileGateStep(
      stepOrPath
    );

  return `/profile-gate/${step}`;
}

// =========================================================
// LOCAL RESUME STORAGE
// =========================================================

export function saveProfileGateResumeStep(
  stepOrPath,
  metadata = {}
) {
  try {
    const step =
      normalizeProfileGateStep(
        stepOrPath
      );

    const path =
      profileGatePathForStep(
        step
      );

    const record = {
      step,
      path,

      updatedAt:
        new Date()
          .toISOString(),

      ...metadata,
    };

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        record
      )
    );

    localStorage.setItem(
      LEGACY_STORAGE_KEY,
      step
    );

    return record;
  } catch {
    return {
      step:
        "welcome",

      path:
        "/profile-gate/welcome",

      updatedAt:
        null,
    };
  }
}

export function saveProfileGateResumeFromPath(
  pathname,
  metadata = {}
) {
  const cleanValue =
    cleanProfileGateValue(
      pathname
    );

  const match =
    cleanValue.match(
      /^profile-gate\/([^/]+)/
    );

  const rawStep =
    match?.[1];

  if (!rawStep) {
    return null;
  }

  return saveProfileGateResumeStep(
    rawStep,
    metadata
  );
}

export function getProfileGateResumeRecord() {
  try {
    const raw =
      localStorage.getItem(
        STORAGE_KEY
      );

    const parsed =
      raw
        ? JSON.parse(
            raw
          )
        : null;

    if (
      parsed?.step
    ) {
      const step =
        normalizeProfileGateStep(
          parsed.step
        );

      return {
        step,

        path:
          profileGatePathForStep(
            step
          ),

        updatedAt:
          parsed.updatedAt ||
          null,
      };
    }
  } catch {}

  try {
    const legacyStep =
      localStorage.getItem(
        LEGACY_STORAGE_KEY
      );

    if (
      legacyStep
    ) {
      const step =
        normalizeProfileGateStep(
          legacyStep
        );

      return {
        step,

        path:
          profileGatePathForStep(
            step
          ),

        updatedAt:
          null,
      };
    }
  } catch {}

  return {
    step:
      "welcome",

    path:
      "/profile-gate/welcome",

    updatedAt:
      null,
  };
}

export function getProfileGateResumeStep() {
  return (
    getProfileGateResumeRecord()
      .step
  );
}

export function getProfileGateResumePath() {
  return (
    getProfileGateResumeRecord()
      .path ||
    "/profile-gate/welcome"
  );
}

// =========================================================
// CURRENT ONBOARDING COMPLETENESS
// =========================================================

function isFocusComplete(
  draft
) {
  return (
    Array.isArray(
      draft
        ?.business_priorities
    ) &&
    draft
      .business_priorities
      .length ===
      1 &&
    hasText(
      draft
        .business_priorities[0]
    )
  );
}

function isBusinessBasicsComplete(
  draft
) {
  return Boolean(
    hasText(
      draft?.business_name
    ) &&
      hasText(
        draft
          ?.business_setup_type
      ) &&
      hasText(
        draft
          ?.trading_duration_range
      ) &&
      hasText(
        draft
          ?.employee_count_range
      ) &&
      hasText(
        draft?.industry
      ) &&
      hasText(
        draft?.province
      )
  );
}

function isFinancialSetupComplete(
  draft
) {
  return Boolean(
    hasText(
      draft
        ?.annual_revenue_range
    ) &&
      hasText(
        draft
          ?.finance_tracking_method
      ) &&
      hasText(
        draft
          ?.business_banking_type
      )
  );
}

function isLearningPlanComplete(
  draft
) {
  return (
    hasExactly(
      draft
        ?.learning_days,
      3
    ) &&
    hasExactly(
      draft
        ?.reminder_times,
      2
    )
  );
}

// =========================================================
// V2 NORMALISATION
// =========================================================

export function normalizeProfileGateV2Step(
  stepOrPath
) {
  return normalizeProfileGateStep(
    stepOrPath
  );
}

export function profileGateV2PathForStep(
  stepOrPath
) {
  return profileGatePathForStep(
    stepOrPath
  );
}

// =========================================================
// DURABLE DATABASE RESUME
// =========================================================

export function getProfileGateV2ResumeStep(
  draft = {}
) {
  const databaseStep =
    String(
      draft
        ?.resume_step ||
        ""
    ).trim();

  if (
    databaseStep
  ) {
    return normalizeProfileGateStep(
      databaseStep
    );
  }

  return "welcome";
}

// =========================================================
// SMART RESUME
// =========================================================

export function getValidatedProfileGateV2ResumePath(
  draft = {}
) {
  // Final durable completion marker.
  if (
    draft
      ?.onboarding_completed_at
  ) {
    return "/dashboard";
  }

  const requested =
    getProfileGateV2ResumeStep(
      draft
    );

  // -------------------------------------------------------
  // Focus
  // -------------------------------------------------------

  if (
    !isFocusComplete(
      draft
    )
  ) {
    if (
      requested ===
      "welcome"
    ) {
      return "/profile-gate/welcome";
    }

    if (
      requested ===
      "focus"
    ) {
      return "/profile-gate/focus";
    }

    return "/profile-gate/get-started";
  }

  // -------------------------------------------------------
  // Business basics
  // -------------------------------------------------------

  if (
    !isBusinessBasicsComplete(
      draft
    )
  ) {
    return "/profile-gate/business-basics";
  }

  // -------------------------------------------------------
  // Financial setup
  // -------------------------------------------------------

  if (
    !isFinancialSetupComplete(
      draft
    )
  ) {
    return "/profile-gate/financial-setup";
  }

  // -------------------------------------------------------
  // Summary
  // -------------------------------------------------------

  if (
    requested ===
    "summary"
  ) {
    return "/profile-gate/summary";
  }

  // -------------------------------------------------------
  // Learning plan
  // -------------------------------------------------------

  if (
    !isLearningPlanComplete(
      draft
    )
  ) {
    if (
      requested ===
      "financial-setup"
    ) {
      return "/profile-gate/summary";
    }

    return "/profile-gate/learning-plan";
  }

  // -------------------------------------------------------
  // Orientation
  // -------------------------------------------------------

  if (
    requested ===
    "orientation"
  ) {
    return "/profile-gate/orientation";
  }

  // -------------------------------------------------------
  // Completion
  // -------------------------------------------------------

  if (
    requested ===
    "onboarding-complete"
  ) {
    return "/profile-gate/onboarding-complete";
  }

  // -------------------------------------------------------
  // Consent
  // -------------------------------------------------------

  if (
    requested ===
    "consent-notices"
  ) {
    return "/profile-gate/consent-notices";
  }

  return "/profile-gate/onboarding-complete";
}

// =========================================================
// LEGACY EXPORT
// =========================================================

export function getValidatedProfileGateResumePath(
  draft = {}
) {
  return getValidatedProfileGateV2ResumePath(
    draft
  );
}

// =========================================================
// CLEAR LOCAL RESUME
// =========================================================

export function clearProfileGateResumeStep() {
  try {
    localStorage.removeItem(
      STORAGE_KEY
    );

    localStorage.removeItem(
      LEGACY_STORAGE_KEY
    );
  } catch {}
}